import React, { FC, useCallback, useState, ReactNode } from 'react';
import style from './style.module.less';
import {
  Message,
  MessageType,
  Lawyer,
  LawyerConnectionPermissions,
} from '@law-connect/types';
import {
  LawyerChatMessage,
  UserChatMessage,
} from '@law-connect/react-components';
import WarningCircleIcon from './warning-circle.svg?react';
import { UserInfoModal } from '../../components/user-info-modal';
import { useAppSelector } from '../../redux/hooks';
import selectors from '../../redux/selectors';

import TickCircleIcon from './tick-circle.svg?react';
import CrossCircleIcon from './cross-circle.svg?react';
import { useTranslation } from 'react-i18next';

interface Props {
  messages: Message[];
  message: Message;
  connectionId: string;
  avatar?: ReactNode;
  avatarClassName?: string;
  wrapperClassName?: string;
  timestamp?: string | ReactNode;
  sender?: ReactNode | string;
  lawyerFirstName?: string;
  startHover: () => void;
  endHover: () => void;
}

export const PermissionMessage: FC<Props> = (props) => {
  const {
    message,
    messages,
    connectionId,
    avatar,
    avatarClassName,
    wrapperClassName,
    timestamp,
    sender,
    lawyerFirstName,
    endHover,
    startHover,
  } = props;
  const user = useAppSelector(selectors.lawyerConnection.getUser(connectionId));
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false);

  const openUserInfoModal = useCallback(() => setIsUserInfoModalOpen(true), []);
  const closeUserInfoModal = useCallback(
    () => setIsUserInfoModalOpen(false),
    []
  );
  const { t } = useTranslation();

  if (
    message.type === MessageType.Permission ||
    message.type === MessageType.ContactInformation
  ) {
    if (message.from === 'lawyer') {
      let closingMessage: Message | null = null;
      let permissionGranted: boolean = false;
      let requestMessage: JSX.Element | null = null;

      if (message.type === MessageType.Permission) {
        closingMessage = messages?.find(
          (m) =>
            m.type === MessageType.Permission &&
            m.from === 'client' &&
            m.content.requestMessageId === message.id
        );
        permissionGranted = (
          closingMessage?.content as {
            permissions: LawyerConnectionPermissions;
          }
        )?.permissions?.userDetails;
        requestMessage = (
          <>
            {t('connect.lawyer-requested-private-information').replace(
              'LAWYER_NAME',
              lawyerFirstName ?? t('connect.you')
            )}
          </>
        );
      } else if (message.type === MessageType.ContactInformation) {
        closingMessage = messages?.find(
          (m) =>
            m.type === MessageType.ContactInformation &&
            m.from === 'client' &&
            m.content.requestMessageId === message.id
        );
        permissionGranted = (
          closingMessage?.content as {
            permissions: LawyerConnectionPermissions;
          }
        )?.permissions?.phoneNumber;
        requestMessage = (
          <>
            {t('connect.lawyer-requested-contact-information').replace(
              'LAWYER_NAME',
              lawyerFirstName ?? t('connect.you')
            )}
          </>
        );
      }
      const state = !closingMessage
        ? 'Pending'
        : permissionGranted
          ? 'Granted'
          : 'Denied';

      return (
        <div className={`${style.messageWrapper} ${wrapperClassName ?? ''}`}>
          <div>
            {sender ? (
              <div
                className={`${style.senderContainer}
              ${message.from === 'lawyer' ? style.right : style.left}`}
              >
                {sender}
              </div>
            ) : null}
            <UserChatMessage
              timestamp={message.createdAt}
              className={style.message}
              onMouseEnter={startHover}
              onMouseLeave={endHover}
            >
              <div className={style.iconContainer}>
                <WarningCircleIcon className={style.icon} />
              </div>
              {requestMessage}
              <b> {state}</b>.
            </UserChatMessage>
            {timestamp ? (
              <div className={`${style.timestampContainer}`}>{timestamp}</div>
            ) : null}
          </div>
          <div className={`${avatarClassName ?? ''}`}>{avatar}</div>
        </div>
      );
    } else {
      const permissions: LawyerConnectionPermissions =
        message.content.permissions;

      const permissionAllowed =
        message.type === MessageType.Permission
          ? permissions.userDetails
          : permissions.phoneNumber;

      const state = permissionAllowed
        ? t('connect.granted').toLocaleLowerCase()
        : t('connect.denied').toLocaleLowerCase();
      let messageRequested: JSX.Element | null = null;
      if (message.type === MessageType.Permission) {
        messageRequested = (
          <>
            Access to&nbsp;
            <span onClick={openUserInfoModal} className={style.link}>
              private information
            </span>
          </>
        );
      } else if (message.type === MessageType.ContactInformation) {
        messageRequested = <>{t('connect.access-contact-information')}</>;
      }
      // is from client
      return (
        <div className={`${style.messageWrapper} ${wrapperClassName ?? ''}`}>
          <div className={`${avatarClassName ?? ''}`}>{avatar}</div>
          <div>
            {sender ? (
              <div
                className={`${style.senderContainer}
              ${style.left}`}
              >
                {sender}
              </div>
            ) : null}
            <LawyerChatMessage
              timestamp={message.createdAt}
              className={`${style.message} ${style.sender}`}
              onMouseEnter={startHover}
              onMouseLeave={endHover}
            >
              <div className={style.lawyerIconContainer}>
                {state === 'granted' ? (
                  <TickCircleIcon className={style.icon} />
                ) : (
                  <CrossCircleIcon className={style.icon} />
                )}
              </div>
              {messageRequested}
              &nbsp;
              <b>{state}</b>.
            </LawyerChatMessage>
          </div>
          <UserInfoModal
            closeModal={closeUserInfoModal}
            isOpen={isUserInfoModalOpen}
            user={user}
          />
        </div>
      );
    }
  }
};
